import type { Nursery } from '../types/auth';

/**
 * Utility functions for nursery-aware navigation
 */

/**
 * Extract nursery ID from a nursery-specific URL
 * @param pathname - The URL pathname to parse
 * @returns The nursery ID if found, null otherwise
 */
export const extractNurseryIdFromPath = (pathname: string): string | null => {
  const match = pathname.match(/^\/nursery\/([^\/]+)/);
  return match ? match[1] : null;
};

/**
 * Extract the page path from a nursery-specific URL
 * @param pathname - The URL pathname to parse
 * @returns The page path (e.g., 'dashboard', 'cameras') if found, null otherwise
 */
export const extractPageFromPath = (pathname: string): string | null => {
  const match = pathname.match(/^\/nursery\/[^\/]+\/(.+)$/);
  return match ? match[1] : null;
};

/**
 * Check if a path is a nursery-specific route
 * @param pathname - The URL pathname to check
 * @returns True if it's a nursery route, false otherwise
 */
export const isNurseryRoute = (pathname: string): boolean => {
  return pathname.startsWith('/nursery/');
};

/**
 * Build a nursery-specific URL for a given page
 * @param nurseryId - The nursery ID
 * @param page - The page path (e.g., 'dashboard', 'cameras')
 * @returns The complete URL path
 */
export const buildNurseryUrl = (nurseryId: string, page: string = 'dashboard'): string => {
  return `/nursery/${nurseryId}/${page}`;
};

/**
 * Convert a legacy URL to a nursery-specific URL
 * @param legacyPath - The legacy path (e.g., '/dashboard', '/cameras')
 * @param nurseryId - The nursery ID to use
 * @returns The new nursery-specific URL
 */
export const convertLegacyUrl = (legacyPath: string, nurseryId: string): string => {
  // Remove leading slash if present
  const page = legacyPath.startsWith('/') ? legacyPath.substring(1) : legacyPath;
  return buildNurseryUrl(nurseryId, page || 'dashboard');
};

/**
 * Get the current nursery from URL or fallback to provided nursery
 * @param pathname - Current URL pathname
 * @param nurseries - Available nurseries
 * @param fallbackNursery - Fallback nursery if URL doesn't contain one
 * @returns The nursery that should be active
 */
export const getActiveNursery = (
  pathname: string, 
  nurseries: Nursery[], 
  fallbackNursery?: Nursery | null
): Nursery | null => {
  const nurseryIdFromUrl = extractNurseryIdFromPath(pathname);
  
  if (nurseryIdFromUrl) {
    const nurseryFromUrl = nurseries.find(c => c.nursery_id === nurseryIdFromUrl);
    if (nurseryFromUrl) {
      return nurseryFromUrl;
    }
  }
  
  return fallbackNursery || (nurseries.length > 0 ? nurseries[0] : null);
};

/**
 * Navigate to the same page but with a different nursery
 * @param currentPath - Current URL pathname
 * @param newNurseryId - The new nursery ID to navigate to
 * @returns The new URL to navigate to
 */
export const switchNurseryInUrl = (currentPath: string, newNurseryId: string): string => {
  const pagePath = extractPageFromPath(currentPath);
  return buildNurseryUrl(newNurseryId, pagePath || 'dashboard');
}; 